import { Fragment, useMemo } from 'react'
import { FormBody, FormCols, FormSection, Textarea } from '../../components/Form'
import FileField from '../../components/FileField'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { questionsStrings as S } from './strings'

const OPTION_KEYS = ['option1', 'option2', 'option3', 'option4']
const OPTION_KEYS_EN = ['option1En', 'option2En', 'option3En', 'option4En']

/**
 * The question's fields — the same set wherever a question is written: on its own page
 * from the bank, and in the topic wizard's sheet.
 *
 * `الإجابة المختارة` offers the four options by their own text rather than by
 * number, so choosing the right answer means reading the answer — the legacy
 * asks you to map "3" onto the third box yourself. Options not yet typed are
 * left out of that list rather than offered as blanks.
 *
 * @param {(key: string) => (value: unknown) => void} set
 * @param {{imageAr?: string, imageEn?: string}} [stored]  the images already saved, drawn
 *   beside the picker so a replacement is a before and an after
 * @param {boolean} askTopic  offer the topic picker — see `validateQuestion` in questionForm.js
 */
export default function QuestionFields({ values, errors, set, categories, levels, stored, askTopic }) {
  /* Only options that actually carry text can be the accepted one. */
  const answerOptions = useMemo(
    () =>
      OPTION_KEYS.map((key, index) => ({ value: String(index + 1), label: values[key]?.trim() }))
        .filter((option) => option.label),
    [values],
  )

  return (
    <FormBody>
      <FormSection title={S.form.question}>
        {/* The two languages side by side rather than one under the other: an
            option and its translation have to be read together, and stacked
            they are eight fields you match up by counting. */}
        <FormCols>
          <FormRow label={S.form.questionAr} error={errors.text}>
            {(id) => (
              <Textarea id={id} rows={3} value={values.text} onChange={set('text')} invalid={!!errors.text} />
            )}
          </FormRow>
          <FormRow label={S.form.questionEn} error={errors.textEn}>
            {(id) => (
              <Textarea
                id={id}
                dir="ltr"
                rows={3}
                value={values.textEn}
                onChange={set('textEn')}
                invalid={!!errors.textEn}
              />
            )}
          </FormRow>

          <FormRow label={S.form.imageAr}>
            {(id) => (
              <FileField id={id} accept="image/*" currentUrl={stored?.imageAr} onChange={set('imageAr')} />
            )}
          </FormRow>
          <FormRow label={S.form.imageEn}>
            {(id) => (
              <FileField id={id} accept="image/*" currentUrl={stored?.imageEn} onChange={set('imageEn')} />
            )}
          </FormRow>
        </FormCols>
      </FormSection>

      <FormSection title={S.form.options}>
        <FormCols>
          {OPTION_KEYS.map((key, index) => (
            <Fragment key={key}>
              <FormRow label={`${S.columns[`option${index + 1}`]} — ${S.form.optionsAr}`} error={errors[key]}>
                {(id) => (
                  <TextField
                    id={id}
                    value={values[key]}
                    onChange={set(key)}
                    invalid={!!errors[key]}
                    autoComplete="off"
                  />
                )}
              </FormRow>
              <FormRow
                label={`${S.columns[`option${index + 1}`]} — ${S.form.optionsEn}`}
                error={errors[OPTION_KEYS_EN[index]]}
              >
                {(id) => (
                  <TextField
                    id={id}
                    dir="ltr"
                    value={values[OPTION_KEYS_EN[index]]}
                    onChange={set(OPTION_KEYS_EN[index])}
                    invalid={!!errors[OPTION_KEYS_EN[index]]}
                    autoComplete="off"
                  />
                )}
              </FormRow>
            </Fragment>
          ))}
        </FormCols>
      </FormSection>

      <FormSection title={S.form.classification}>
        <FormCols>
          <FormRow label={S.form.chosenAnswer} error={errors.answer}>
            {(id) => (
              <Select
                id={id}
                value={values.answer}
                onChange={set('answer')}
                options={answerOptions}
                placeholder={S.columns.answer}
                invalid={!!errors.answer}
              />
            )}
          </FormRow>

          <FormRow label={S.form.difficulty} error={errors.difficulty}>
            {(id) => (
              <Select
                id={id}
                value={values.difficulty}
                onChange={set('difficulty')}
                options={levels}
                placeholder={S.columns.difficulty}
                invalid={!!errors.difficulty}
              />
            )}
          </FormRow>

          {/* NOT OFFERED INSIDE A TOPIC. Asking which topic a question belongs to while
              standing in that topic invites the one answer that is wrong — filing it
              somewhere else — and the server would overrule the choice anyway. */}
          {askTopic && (
            <FormRow label={S.form.category} error={errors.category}>
              {(id) => (
                <Select
                  id={id}
                  value={values.category}
                  onChange={set('category')}
                  options={categories}
                  placeholder={S.columns.category}
                  invalid={!!errors.category}
                />
              )}
            </FormRow>
          )}
        </FormCols>
      </FormSection>
    </FormBody>
  )
}
